import { useRef, useState } from 'react';
import { LandingWrapper } from '../../ui/components/Landing/LandingWrapper/LandingWrapper';
import { LandingStartBlock } from '../../ui/components/Landing/LandingStartBlock/LandingStartBlock';
import ImageJessica from '../../assets/images/unique/LogoRemastered.png';

import styles from './Landing.module.scss';
import { LandingSmallCards } from '../../ui/components/Landing/LandingSmallCards/LandingSmallCards';
import { SmallCardProps } from '../../ui/components/SmallCard/SmallCard';
import { LandingServices } from '../../ui/components/Landing/LandingServices/LandingServices';
import { LandingMap } from '../../ui/components/Landing/LandingMap/LandingMap';
import { MenuItem } from '../../interfaces/Menu.interfaces';
import { DropdownItemsProps } from '../../ui/components/Dropdown/Dropdown';
import { JESSICA_SERVICES } from '../components/AppointmentModal/Services';
import { LandingReviews } from '../../ui/components/Landing/LandingReviews/LandingReviews';
import { AppointmentModalV2 } from '../components/AppointmentModalV2/AppointmentModalV2';

export const Landing = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [defaultService, setDefaultService] =
    useState<DropdownItemsProps>(null);
  const serviceRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const social = {
    whatsapp: {
      link: 'https://wa.me/3310504287',
    },
    facebook: {
      link: 'https://www.facebook.com/drjessicaflores',
    },
  };
  const openAppointmentFor = (serviceName: string) => {
    setDefaultService(
      JESSICA_SERVICES.find((service) => service.value === serviceName) || null
    );
    setIsOpen(true);
  };
  const smallCards: SmallCardProps[] = [
    {
      bigText: '15+',
      smallText2: 'Years of experience',
    },
    {
      bigText: '2',
      smallText2: 'Medicine Diplomas',
    },
    {
      bigText: '4',
      smallText2: 'Featured notes',
    },
    {
      bigText: 'UAG',
      smallText2: "Bachelor's Degree",
      theme: 'secondary',
    },
    {
      bigText: '1K+',
      smallText2: 'Happy Patients',
    },
  ];

  const menuItems: MenuItem[] = [
    {
      endpoint: '/',
      icon: 'bookmark',
      name: 'Articles',
      goToRef: serviceRef,
    },
    {
      endpoint: '/location',
      icon: 'location',
      name: 'Find us!',
      goToRef: locationRef,
    },
  ];
  return (
    <LandingWrapper
      menuItems={menuItems}
      social={social}
      navbarColorPalette="doctor-1"
      footerColorPalette="doctor-2"
    >
      <AppointmentModalV2
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        defaultService={defaultService}
      />
      <LandingStartBlock
        colorPalette="doctor-1"
        documentTitle="Doctor Jessica Flores"
        eyebrow="Functional and orthomolecular medicine"
        title="Doctor Jessica Flores"
        subtitle="Discover your optimal well-being"
        description="Personalized medical care focused on prevention, balance, and long-term vitality."
        button1={{
          text: 'Book an appointment',
          btnStyle: 'primary',
          glassEffect: true,
          onClick: () => {
            setIsOpen(true);
            setDefaultService(null);
          },
        }}
        button2={{
          text: 'Read wellness notes',
          btnStyle: 'secondary',
          glassEffect: true,
          onClick: () => {
            serviceRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          },
        }}
        localChildren={
          <img
            className={styles.image}
            src={ImageJessica}
            alt="Doctor's Logo"
          />
        }
      />
      <LandingSmallCards
        documentTitle="Experience"
        colorPalette="doctor-1"
        smallCards={smallCards}
      />
      <LandingServices
        documentTitle="Wellness Journal"
        colorPalette="doctor-3"
        title="Wellness notes"
        ref={serviceRef}
        serviceCards={[
          {
            title: 'Hormonal balance through different life stages',
            text: `A practical note about how hormonal changes can affect energy, sleep, mood, and long-term wellness conversations.`,
            button1: {
              text: 'Read article',
              to: '/wellness-notes/hormone-replacement',
            },
            button2: {
              text: 'Book appointment',
              btnStyle: 'secondary',
              onClick: () => {
                openAppointmentFor('Hormone Replacement');
              },
            },
          },
          {
            title: 'What to know about platelet-rich plasma',
            text: `An educational overview of platelets, tissue repair, and why regenerative medicine conversations are highly individual.`,
            button1: {
              text: 'Read article',
              to: '/wellness-notes/platelet-rich-plasma',
            },
            button2: {
              text: 'Book appointment',
              btnStyle: 'secondary',
              onClick: () => {
                openAppointmentFor('Platelet Rich Plasma');
              },
            },
          },
          {
            title: 'Cellular repair and stem cell concepts',
            text: `A concise article about cellular health, recovery language, and the questions worth bringing to a medical consultation.`,
            button1: {
              text: 'Read article',
              to: '/wellness-notes/stem-cells',
            },
            button2: {
              text: 'Book appointment',
              btnStyle: 'secondary',
              onClick: () => {
                openAppointmentFor('Stem Cells');
              },
            },
          },
          {
            title: 'Pain, mobility, and daily comfort',
            text: `A wellness note about pain patterns, function, and how a personalized assessment can guide next steps.`,
            button1: {
              text: 'Read article',
              to: '/wellness-notes/pain-control-management',
            },
            button2: {
              text: 'Book appointment',
              btnStyle: 'secondary',
              onClick: () => {
                openAppointmentFor('Neural Therapy');
              },
            },
          },
          {
            title: 'Read more notes',
            button1: {
              text: 'Open article index',
              to: '/wellness-notes',
            },
          },
        ]}
      />
      <LandingReviews colorPalette="doctor-2" />
      <LandingMap
        documentTitle="Location"
        colorPalette="doctor-2"
        ref={locationRef}
        lat={20.2994652}
        lng={-103.2562367}
        title="Visit Doctor Jessica Flores in Ajijic"
        direction="Guadalupe Victoria 101-Interior C, Centro, 45920 Ajijic, Jal."
        text="Visit us, We are near the center of Ajijic"
        social={social}
        googleLink="https://www.google.com/maps/place/Dr.+Jessica+Flores/@20.2994652,-103.2588116,17z/data=!3m1!4b1!4m6!3m5!1s0x842f47d22dcec52b:0xee6e8e9dfa1f1080!8m2!3d20.2994652!4d-103.2562367!16s%2Fg%2F11f5rpssp0?hl=es-419&entry=ttu"
      />
    </LandingWrapper>
  );
};

// <Icon type="medical" alt="Medical" classname={styles.icon} />
